// Layer 3 (Transformation): the plant model's flow strip (polish.P11, comp
// `.impeccable/mocks/decision/plant-model.html` variant A, picked 25 Sep).
// The Advisory strip idiom applied to the plant's fixed stage order: crusher
// → paste, with water as the utility cell. Every figure is read from an
// existing projection — the circuit tiles (`circuitsView`, the SPC read of
// each circuit's outcome), the plant strip (`plantStrip`, headlines and the
// bottleneck share) and the sample engine (`sampleChips`, the stage that
// takes each sample). Nothing here computes a new number.

import type { SignalCell } from '@/versions/v1/schema/spc';
import type { SampleAdherence } from '@/versions/v1/schema/unit-operation';
import type { ScenarioPhase } from '@/versions/v1/schema/scenario';
import { subprocessForNodeId } from '@/versions/v1/shared/bunker-hill-plant-nodes';
import { CIRCUITS, CONTEXT_STUBS, type CircuitId, type CircuitTile, type CircuitsView } from './Circuits.transform';
import { bottleneckLabel, bottleneckTone } from './ProcessGraph.transform';
import type { PlantStripNode, SampleChip } from './Scene.transform';

export type FlowTone = 'ok' | 'warn' | 'bad';

/** The drift panel's meaning words — never the colour the dot already says. */
export const SIGNAL_CELL_LABEL: Partial<Record<SignalCell, string>> = {
  drift: 'Drift',
  consequence: 'Consequence',
  'not-capable': 'Not capable',
};

export const SIGNAL_CELL_TONE: Partial<Record<SignalCell, 'warn' | 'bad'>> = {
  drift: 'warn',
  consequence: 'bad',
  'not-capable': 'warn',
};

const CIRCUIT_RAG_TONE: Record<CircuitTile['rag'], FlowTone> = {
  green: 'ok',
  amber: 'warn',
  red: 'bad',
};

/** The outcome reading's short name where the unit alone would not say it. */
const OUTCOME_SHORT: Record<string, string> = {
  'rd-spec-p80': 'P80',
};

export type FlowSampleFlag = {
  id: string;
  word: SampleAdherence;
  tone: 'warn' | 'bad';
  title: string;
};

type FlowCellBase = {
  id: string;
  name: string;
  /** Off-routine samples the stage takes (MISS / LATE / PENDING); REG ones stay in the panel. */
  samples: readonly FlowSampleFlag[];
};

export type FlowCircuitCell = FlowCellBase & {
  kind: 'circuit';
  circuitId: CircuitId;
  /** The circuit's SPC read of its outcome at the cursor (the strip dot). */
  status: FlowTone;
  figure: string;
  figureContext: string;
  comparator: string | null;
  stagesLine: string | null;
  /** Bottleneck share of band — the grinding cell only. */
  util: { pct: number; label: string; tone: 'warn' | 'bad' } | null;
  tag: { label: string; tone: 'warn' | 'bad' } | null;
  drift: { word: string; tone: 'warn' | 'bad'; label: string } | null;
  quietLine: string | null;
};

export type FlowContextCell = FlowCellBase & {
  kind: 'context' | 'utility';
  headline: string;
  sub: string | null;
};

export type FlowCell = FlowCircuitCell | FlowContextCell;

const CONTEXT_NAME = new Map(CONTEXT_STUBS.map((stub) => [stub.id, stub.label]));

/** Stage that takes a sample: the host node resolved to its subprocess. */
export function sampleStageId(sample: SampleChip): string {
  return subprocessForNodeId(sample.hostNodeId) ?? sample.hostNodeId;
}

function sampleFlags(stageId: string, samples: readonly SampleChip[]): FlowSampleFlag[] {
  return samples
    .filter((sample) => sampleStageId(sample) === stageId && sample.adherence !== 'REG')
    .map((sample) => ({
      id: sample.id,
      word: sample.adherence,
      tone: sample.adherence === 'MISS' ? ('bad' as const) : ('warn' as const),
      title: `${sample.id} · ${sample.stream} · ${sample.spec}`,
    }));
}

function comparatorFor(tile: CircuitTile): string | null {
  const { basisLabel, basisValue, unit } = tile.outcome;
  if (!basisLabel || basisValue === undefined) return null;
  const unitSuffix = unit.startsWith('%') ? '' : ` ${unit}`;
  return `${basisLabel} ${basisValue.toLocaleString()}${unitSuffix}`;
}

/** The seven strip cells in plant order (the plant strip's own order). */
export function plantFlowCells(input: {
  strip: readonly PlantStripNode[];
  circuits: CircuitsView;
  samples: readonly SampleChip[];
  phase: ScenarioPhase;
}): FlowCell[] {
  return input.strip.map((node): FlowCell => {
    const circuit = CIRCUITS.find((row) => row.subprocessId === node.id);
    const tile = circuit ? input.circuits.circuits.find((row) => row.id === circuit.id) : undefined;
    const samples = sampleFlags(node.id, input.samples);
    if (circuit && tile) {
      const tone = bottleneckTone(node, input.phase);
      const tagLabel = bottleneckLabel(node, input.phase);
      const util =
        tone && node.utilizationPct != null
          ? { pct: node.utilizationPct, label: `${node.utilizationPct}% of band`, tone }
          : null;
      const cellWord = SIGNAL_CELL_LABEL[tile.worstCell as SignalCell];
      const cellTone = SIGNAL_CELL_TONE[tile.worstCell as SignalCell];
      const short = OUTCOME_SHORT[circuit.outcomeReadingId];
      return {
        kind: 'circuit',
        id: node.id,
        circuitId: circuit.id,
        name: circuit.label,
        status: CIRCUIT_RAG_TONE[tile.rag],
        figure: tile.outcome.value,
        figureContext: short ? `${tile.outcome.unit} ${short}` : tile.outcome.unit,
        comparator: comparatorFor(tile),
        // The bottleneck cell carries its share-of-band row in the stage-names slot.
        stagesLine: util ? null : tile.stageNamesLine,
        util,
        tag: tone && tagLabel ? { label: tagLabel, tone } : null,
        drift:
          tile.rag !== 'green' && cellWord && cellTone
            ? { word: cellWord, tone: cellTone, label: tile.worstLabel ?? '' }
            : null,
        quietLine: tile.rag === 'green' ? `in band · ${tile.variableCount} variables` : null,
        samples,
      };
    }
    const sub = node.utility ? node.recycleBadge : node.sub;
    return {
      kind: node.utility ? 'utility' : 'context',
      id: node.id,
      name: CONTEXT_NAME.get(node.id) ?? node.shortName,
      headline: node.headline ?? '—',
      sub: sub ? sub.split('\n').join(' · ') : null,
      samples,
    };
  });
}

/** The strip cell a deep link names: a subprocess id or a short circuit id. */
export function flowCellIdForParam(raw: string | null): string | undefined {
  if (!raw) return undefined;
  const circuit = CIRCUITS.find((row) => row.id === raw || row.subprocessId === raw);
  if (circuit) return circuit.subprocessId;
  return raw.startsWith('sp-') ? raw : undefined;
}

/** The circuit a stage belongs to, if it is one of the three. */
export function circuitForStage(stageId: string): CircuitId | undefined {
  return CIRCUITS.find((row) => row.subprocessId === stageId)?.id;
}

export type CircuitBracketView = {
  id: string;
  nodeId: string;
  label: string;
  status: FlowTone;
  word: string | null;
  wordTone: 'warn' | 'bad' | null;
  x: number;
  y: number;
  w: number;
};

/** Bracket top above the node top — clears the loop arcs and the Pb concentrate over-arc drawn there. */
export const BRACKET_RISE = 90;
export const BRACKET_H = 24;
const BRACKET_OVERHANG = 16;

/** B's circuit brackets for the process-graph view: each circuit drawn over
 *  its node with the strip cell's dot and drift word, so the two views share
 *  one status vocabulary. */
export function circuitBrackets(
  cells: readonly FlowCell[],
  boxes: readonly { id: string; x: number; y: number; w: number }[],
): CircuitBracketView[] {
  return cells.flatMap((cell) => {
    if (cell.kind !== 'circuit') return [];
    const box = boxes.find((row) => row.id === cell.id);
    if (!box) return [];
    return [
      {
        id: `bracket-${cell.circuitId}`,
        nodeId: cell.id,
        label: cell.name,
        status: cell.status,
        word: cell.drift?.word ?? null,
        wordTone: cell.drift?.tone ?? null,
        // The bracket reaches a little past its node so the label
        // ("LEAD FLOTATION · DRIFT") sits inside it without wrapping.
        x: box.x - BRACKET_OVERHANG,
        y: box.y - BRACKET_RISE,
        w: box.w + 2 * BRACKET_OVERHANG,
      },
    ];
  });
}
